import { z } from 'zod';
import { queryBoolean } from '../api/query-boolean';

/**
 * The Groups service's public contract.
 *
 * A membership references a person by id and nothing more. Names for a roster
 * are resolved through People's read port, so widening a Person never widens
 * what Groups can see, and Groups never learns a birth date it has no business
 * with.
 */

export const GROUP_STATUSES = ['ACTIVE', 'INACTIVE', 'ARCHIVED'] as const;
export type GroupStatus = (typeof GROUP_STATUSES)[number];

export const GROUP_MEMBER_ROLES = ['MEMBER', 'LEADER', 'CO_LEADER', 'APPRENTICE'] as const;
export type GroupMemberRole = (typeof GROUP_MEMBER_ROLES)[number];

export const MEETING_DAYS = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
] as const;
export type MeetingDay = (typeof MEETING_DAYS)[number];

// --- Requests ---------------------------------------------------------------

const groupNameField = z.string().trim().min(1).max(150);

/** Local wall-clock time, `HH:MM`. Not an instant — see the Prisma model. */
const meetingTimeField = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected a time as HH:MM');

export const createGroupRequestSchema = z.object({
  name: groupNameField,
  description: z.string().trim().max(2000).nullish(),
  campusId: z.uuid().nullish(),
  organizationId: z.uuid().nullish(),
  /** Null means no limit. Zero would mean a group nobody can join. */
  capacity: z.number().int().positive().nullish(),
  meetingDay: z.enum(MEETING_DAYS).nullish(),
  meetingTime: meetingTimeField.nullish(),
  meetingLocation: z.string().trim().max(200).nullish(),
  /** Where the pin for this group sits on the groups map — same range
   *  Tenant's own address picker validates against. */
  latitude: z.number().min(-90).max(90).nullish(),
  longitude: z.number().min(-180).max(180).nullish(),
  /** The cell this one reports into, for the groups org chart. */
  parentGroupId: z.uuid().nullish(),
});
export type CreateGroupRequest = z.infer<typeof createGroupRequestSchema>;

/**
 * Declared field by field rather than as `createGroupRequestSchema.partial()`.
 *
 * `.partial()` keeps `.default()` values, so an empty PATCH would parse to a
 * body full of defaults and quietly overwrite real data with them. People paid
 * for that lesson once; it is not repeated here.
 */
export const updateGroupRequestSchema = z
  .object({
    name: groupNameField.optional(),
    description: z.string().trim().max(2000).nullish(),
    campusId: z.uuid().nullish(),
    organizationId: z.uuid().nullish(),
    capacity: z.number().int().positive().nullish(),
    meetingDay: z.enum(MEETING_DAYS).nullish(),
    meetingTime: meetingTimeField.nullish(),
    meetingLocation: z.string().trim().max(200).nullish(),
    latitude: z.number().min(-90).max(90).nullish(),
    longitude: z.number().min(-180).max(180).nullish(),
    parentGroupId: z.uuid().nullish(),
    status: z.enum(GROUP_STATUSES).optional(),
  })
  .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'At least one field must be supplied',
  });
export type UpdateGroupRequest = z.infer<typeof updateGroupRequestSchema>;

export const archiveGroupRequestSchema = z.object({
  reason: z.string().trim().min(1).max(500),
});
export type ArchiveGroupRequest = z.infer<typeof archiveGroupRequestSchema>;

export const addGroupMemberRequestSchema = z.object({
  personId: z.uuid(),
  role: z.enum(GROUP_MEMBER_ROLES).default('MEMBER'),
});
export type AddGroupMemberRequest = z.infer<typeof addGroupMemberRequestSchema>;

export const updateGroupMemberRequestSchema = z.object({
  role: z.enum(GROUP_MEMBER_ROLES),
});
export type UpdateGroupMemberRequest = z.infer<typeof updateGroupMemberRequestSchema>;

/**
 * A reason is optional here, unlike archiving a group.
 *
 * Leaving a small group is ordinary and usually has no story worth recording;
 * closing one is a decision someone will be asked about later.
 */
export const removeGroupMemberRequestSchema = z.object({
  reason: z.string().trim().max(500).nullish(),
});
export type RemoveGroupMemberRequest = z.infer<typeof removeGroupMemberRequestSchema>;

export const listGroupsQuerySchema = z.object({
  cursor: z.string().nullish(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  search: z.string().trim().max(200).nullish(),
  status: z.enum(GROUP_STATUSES).nullish(),
  campusId: z.uuid().nullish(),
  /** Direct children of one group, for its "sub-groups" list and for the
   *  org chart, which asks for each cluster's own roster level by level. */
  parentGroupId: z.uuid().nullish(),
  includeArchived: queryBoolean().default(false),
});
export type ListGroupsQuery = z.infer<typeof listGroupsQuerySchema>;

// --- Responses --------------------------------------------------------------

export interface GroupSummaryResponse {
  readonly id: string;
  readonly name: string;
  readonly status: GroupStatus;
  readonly campusId: string | null;
  readonly capacity: number | null;
  /** Active memberships only. The number staff actually mean by "how big is it". */
  readonly memberCount: number;
  readonly meetingDay: MeetingDay | null;
  readonly meetingTime: string | null;
  /** Null until someone places this group's pin — the groups map skips it
   *  until then rather than guessing a location. */
  readonly latitude: number | null;
  readonly longitude: number | null;
  /** Null means this group is a root on the org chart. */
  readonly parentGroupId: string | null;
  readonly archivedAt: string | null;
}

export interface GroupDetailResponse extends GroupSummaryResponse {
  readonly description: string | null;
  readonly organizationId: string | null;
  readonly meetingLocation: string | null;
  /** Resolved alongside `parentGroupId` so the detail page can show it
   *  without a second round trip — null whenever `parentGroupId` is. */
  readonly parentGroupName: string | null;
  readonly members: readonly GroupMemberResponse[];
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * A roster entry.
 *
 * `displayName` is resolved through PeopleReadPort at read time rather than
 * copied into the membership row. A denormalised name is wrong the moment
 * someone marries, and a roster showing a former name is the kind of error a
 * congregation notices.
 */
export interface GroupMemberResponse {
  readonly personId: string;
  readonly displayName: string;
  readonly role: GroupMemberRole;
  readonly joinedAt: string;
}

/**
 * What `GroupsReadPort` hands another module: enough to label a reference
 * and know whether it is still usable, not the roster or the schedule. See
 * `PersonReference` for the same reasoning applied to People.
 */
export interface GroupReference {
  readonly id: string;
  readonly name: string;
  readonly isArchived: boolean;
}
